import {
  AddFilled,
  ArrowClockwiseRegular,
  DeleteRegular,
  EditRegular,
} from '@fluentui/react-icons';
import { UserListSorting, getSortedUserLabel } from '@mnspro-cloud/services';
import { Person } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { times } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../Button';
import { TagColorEnum } from '../../../Tags/Tags';
import { CurrentLessonCard } from '../CurrentLessonCard/CurrentLessonCard';
import {
  TeachingUnitEntry,
  TeachingUnitEntryType,
} from '../TeachingUnitEntry/TeachingUnitEntry';

export interface LessonCardClassbookEntryDefinition {
  id: string;
  name: string;
  categoryId: string;
  categoryName: string;
}

export interface LessonCardClassbookEntry {
  id: string;
  author: Person;
  groupId: string;
  affectedUsers: Person[];
  comment: string;
  occurred: string;
  definition: LessonCardClassbookEntryDefinition;
}

export interface ClassbookEntriesLessonCardProps {
  isLoading: boolean;
  entries: LessonCardClassbookEntry[];
  onAddEntryClick(): void;
  onRemoveEntryClick(entry: LessonCardClassbookEntry): void;
  onEditEntryClick(entry: LessonCardClassbookEntry): void;
  onRefresh?(): void;
  disabled?: boolean;
  preferenceSorting?: UserListSorting;
}

export const ClassbookEntriesLessonCard: React.FC<
  ClassbookEntriesLessonCardProps
> = ({
  isLoading,
  entries,
  onAddEntryClick,
  onRemoveEntryClick,
  onEditEntryClick,
  onRefresh,
  disabled,
  preferenceSorting,
}) => {
  const { t } = useTranslation();
  return (
    <CurrentLessonCard
      title={t('classbook:classbookentry_title', { defaultValue: 'Klassenbucheinträge' })}
      refreshButton={
        onRefresh && !disabled
          ? {
              icon: <ArrowClockwiseRegular />,
              onClick: onRefresh,
              disabled: isLoading,
            }
          : undefined
      }
      actionButton={
        <Button
          primaryTransparent
          aria-label={t('classbook:classbookentry_new_label', { defaultValue: 'Öffnet Popup zum Anlegen eines neuen Klassenbucheintrags' })}
          disabled={disabled || isLoading}
          icon={<AddFilled />}
          onClick={onAddEntryClick}
        >
          {t('classbook:classbookentry_new', { defaultValue: 'Neuer Klassenbucheintrag' })}
        </Button>
      }
    >
      {isLoading &&
        times(2, index => (
          <TeachingUnitEntry
            loading
            key={`classbook-entry-skeleton-${index}`}
            headline=""
            type={TeachingUnitEntryType.ClassBookEntry}
          />
        ))}

      {!isLoading &&
        entries.map(entry => (
          <TeachingUnitEntry
            key={`classbook-entry-${entry.id}`}
            author={entry.author}
            headline={`${entry.definition.name}`}
            subheadline={entry.comment}
            tags={entry.affectedUsers.map(u => ({
              label: getSortedUserLabel(
                u,
                preferenceSorting ?? UserListSorting.FirstName,
              ),
              color: TagColorEnum.Grey,
            }))}
            type={TeachingUnitEntryType.ClassBookEntry}
            onClick={!disabled ? () => onEditEntryClick(entry) : undefined}
            actions={
              !disabled
                ? [
                    {
                      icon: (
                        <EditRegular
                          aria-label={t('classbook:classbookentry_edit_label', { defaultValue: 'Öffnet Popup zum Bearbeiten des Klassenbucheintrags' })}
                        />
                      ),
                      onClick: () => onEditEntryClick(entry),
                    },
                    {
                      icon: (
                        <DeleteRegular
                          aria-label={t('classbook:classbookentry_delete', { defaultValue: 'Öffnet Popup zum Löschen des Klassenbucheintrags' })}
                        />
                      ),
                      onClick: () => onRemoveEntryClick(entry),
                    },
                  ]
                : []
            }
          />
        ))}
    </CurrentLessonCard>
  );
};
